"use client";

import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "react-toastify";
import { usePathname, useSearchParams } from "next/navigation";
import AppWagmiProvider from "@/app/WagmiProvider";
import LoginButton from "./LoginButton";
import ProductCard from "./ProductCard";
import PiRateDisplay from "./PiRateDisplay";
import PiCheckoutInfo from "./PiCheckoutInfo";
import BuyWithPiButton from "./BuyWithPiButton";
import DynamicFieldForm from "./DynamicFieldForm";
import RedemptionInfo from "./RedemptionInfo";
import NowPaymentsButton from "./NowPaymentsButton.jsx";
import FogoPayButton from "./FogoPayButton.jsx";
import BasePayButton from "./BasePayButton.jsx";
import { useAuth } from "@/context/AuthContext";
import { formatProduct } from "@/features/landing/utils/formatProduct";
import usePiBrowser from "@/features/landing/hooks/usePiBrowser";
import { isBlocked } from "@/features/landing/utils/blocklist";
import { useOptionalPiSocketCtx } from "@/context/PiSocketContext";
import { useReferral, getReferralCode } from "@/hooks/useReferral";
import TronPayButton from "./Tronpaybutton";
import PiBrowserCta from "./PiBrowserCta";

// infer fields when a top-up product doesn't provide requiredFields
const isEsim = (p = {}) =>
  p.__source === "esim" ||
  String(p.productType || p.type).toUpperCase() === "ESIM" ||
  "dataGB" in p ||
  "durationDays" in p ||
  (Array.isArray(p.regions) && p.regions.length > 0);

const inferRequiredFields = (p) => {
  if (!p) return [];
  if (isEsim(p)) {
    const fields = ["recipient.email"];
    if (!p.country) fields.push("recipient.country");
    return fields;
  }

  const type = String(p.productType || p.type || "").toUpperCase();
  const looksLikeTopup =
    (type === "TOPUP" || !!p.recipientGets || !!p.topupSubtype) && !isEsim(p);

  if (looksLikeTopup) {
    const fields = ["recipient.msisdn", "recipient.country"];
    if (p.requiresEmail || p.delivery === "EMAIL") fields.push("recipient.email");
    return fields;
  }

  return [];
};

// Build nested object from dot-path keys
function buildNestedObject(fields) {
  const result = {};
  for (const [path, value] of Object.entries(fields)) {
    const keys = path.split(".");
    let cur = result;
    keys.forEach((k, i) => {
      if (i === keys.length - 1) cur[k] = value;
      else {
        if (!cur[k]) cur[k] = {};
        cur = cur[k];
      }
    });
  }
  return result;
}

function formatMoney(value, digits = 2) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return n.toFixed(digits);
}

function normalizeCheckoutIntent(intent) {
  if (!intent) return null;

  const pricing = intent.pricing || {};
  const payment = intent.payment || {};

  const faceValueUSD =
    pricing.faceValueUSD ??
    pricing.baseUSD ??
    intent.faceValueUSD ??
    intent.baseUSD ??
    intent.amountUSD ??
    null;

  const feePercent =
    pricing.feePercent ??
    intent.feePercent ??
    null;

  const feeUSD =
    pricing.feeUSD ??
    intent.feeUSD ??
    null;

  const totalUSD =
    pricing.totalUSD ??
    intent.totalUSD ??
    null;

  const amountMinor =
    payment.amountMinor ??
    intent.amountMinor ??
    null;

  const decimals =
    payment.decimals ??
    intent.decimals ??
    6;

  const currency =
    payment.currency ??
    intent.currency ??
    "USDC";

  return {
    provider: intent.provider || null,
    faceValueUSD,
    feePercent,
    feeUSD,
    totalUSD,
    amountMinor,
    decimals,
    currency,
  };
}

export default function SelectedProductSummary({
  product,
  piRate,
  feePercent,
  user,
  onCancel,
}) {
   useReferral(); // capture ?ref=CODE into localStorage if present
  const [rangeSelection, setRangeSelection] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(null);
  const [showFullDescription, setShowFullDescription] = useState(false);

  const [activeRail, setActiveRail] = useState(null); // "base" | "fogo" | null
  const [checkoutIntent, setCheckoutIntent] = useState(null);

  const role = user?.role;
  const isAdmin = role === "admin";

  // toggle when you want to open it to non-admins later
  const BASE_ENABLED_FOR_ALL = false;

  // Base visible only if logged-in AND (admin OR enabled-for-all)
  const canSeeBase = !!user && (isAdmin || BASE_ENABLED_FOR_ALL);

  const onBaseIntent = useCallback((intent) => {
    setActiveRail("base");
    setCheckoutIntent(intent ? { ...intent, provider: "base" } : null);
  }, []);

  const onFogoIntent = useCallback((intent) => {
    setActiveRail("fogo");
    setCheckoutIntent(intent ? { ...intent, provider: "fogo" } : null);
  }, []);

  const onTronIntent = useCallback((intent) => {
  setActiveRail("tron");
  setCheckoutIntent(intent ? { ...intent, provider: "tron" } : null);
}, []);

  const { setRedirectPath } = useAuth();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const qsAmount = searchParams?.get("amount");
  const qsCurrency = searchParams?.get("currency");
  const isPiBrowser = usePiBrowser();

  const description = useMemo(() => {
    return product?.description ?? product?.fullDetail?.description ?? "";
  }, [product]);

  const redemptionInstructions = useMemo(() => {
    return product?.redemptionInstructions ?? product?.fullDetail?.redemptionInstructions ?? [];
  }, [product]);

  const brandInfoPdf = useMemo(() => {
    return product?.brandInfoPdf ?? product?.fullDetail?.brandInfoPdf ?? "";
  }, [product]);

  const formatted = useMemo(
    () => formatProduct(product, user, { piRate, feePercent }),
    [product, user, piRate, feePercent]
  );

  const hardBlocked = useMemo(
    () => isBlocked({ ...(product || {}), ...(formatted || {}) }),
    [product, formatted]
  );

  const isVisaPrepaidOpen = useMemo(() => {
    const s = `${product?.brandName || ""} ${product?.brand || ""} ${product?.name || ""} ${product?.offerId || ""}`;
    const isVisa = /visa/i.test(s);
    const isAwards = /awards/i.test(s);
    const priceType = String(
      product?.priceType || (formatted?.hasRange ? "RANGE" : "FIXED")
    ).toUpperCase();

    return isVisa && !isAwards && priceType === "RANGE";
  }, [product, formatted?.hasRange]);

  const [visaEmail, setVisaEmail] = useState(user?.email || "");
  const [visaCountry, setVisaCountry] = useState(
    product?.country || product?.fullDetail?.country || "US"
  );

  const priceCurrencyStable = useMemo(
    () => formatted?.priceCurrency || product?.priceCurrency || "USD",
    [formatted?.priceCurrency, product?.priceCurrency]
  );

  useEffect(() => {
    setVisaEmail(user?.email || "");
    setVisaCountry(product?.country || product?.fullDetail?.country || "US");
  }, [user?.email, product?.offerId, product?.country, product?.fullDetail?.country]);

  const handleRangeChange = useCallback(
    (val) => {
      const ccy = priceCurrencyStable;
      setRangeSelection((prev) => {
        if (prev && prev.amount === val && prev.currency === ccy) return prev;
        return {
          amount: val,
          currency: ccy,
          amountUSD: String(ccy).toUpperCase() === "USD" ? val : null,
        };
      });
    },
    [priceCurrencyStable]
  );

  const resolvedRequiredFields = useMemo(() => {
    if (Array.isArray(product?.requiredFields) && product.requiredFields.length) {
      return product.requiredFields;
    }
    return inferRequiredFields(product);
  }, [product]);

  const effectiveCountry = (
    product?.country ||
    product?.fullDetail?.country ||
    "US"
  )?.toUpperCase();

  const referralCode = getReferralCode();

const piBrowserPath = useMemo(() => {
  const id = product?.offerId || product?.sku;
  if (!id) return "/";

  const source = String(product?.__source || product?.productType || product?.type || "")
    .toLowerCase();

  if (source.includes("topup")) return `/topups/offer/${encodeURIComponent(id)}`;
  if (source.includes("esim")) return `/esims/offer/${encodeURIComponent(id)}`;
  return `/vouchers/offer/${encodeURIComponent(id)}`;
}, [product]);

const piBrowserQuery = useMemo(() => {
  const q = {};

  if (referralCode) q.ref = referralCode;

  if (rangeSelection?.amount != null) {
    q.amount = String(rangeSelection.amount);
  }

  if (rangeSelection?.currency) {
    q.currency = String(rangeSelection.currency).toUpperCase();
  }

  return q;
}, [referralCode, rangeSelection?.amount, rangeSelection?.currency]);

  const reqForForm = useMemo(() => {
    let base = effectiveCountry
      ? resolvedRequiredFields.filter((f) => f !== "recipient.country")
      : resolvedRequiredFields;

    if (isVisaPrepaidOpen) {
      base = base.filter((f) => !["recipient.email", "recipient.country"].includes(f));
    }

    return base;
  }, [resolvedRequiredFields, effectiveCountry, isVisaPrepaidOpen]);

  useEffect(() => {
    if (reqForForm.length > 0) {
      setShowForm(true);
      setFormData(null);
    } else {
      setShowForm(false);
      setFormData((prev) => prev ?? {});
    }
  }, [reqForForm]);

  useEffect(() => {
    if (!user) {
      const qs = searchParams?.toString();
      setRedirectPath(`${pathname}${qs ? `?${qs}` : ""}`);
    }
  }, [user, pathname, searchParams, setRedirectPath]);

  const nestedData = useMemo(
    () => (formData ? buildNestedObject(formData) : null),
    [formData]
  );

useEffect(() => {
  if (!formatted?.hasRange) return;

  const min = Number(formatted.priceMinValue);
  const max = Number(formatted.priceMaxValue);

  const parsedAmount = qsAmount != null ? Number(qsAmount) : NaN;
  const parsedCurrency = (qsCurrency || priceCurrencyStable || "USD").toUpperCase();

  const hasValidPrefill =
    Number.isFinite(parsedAmount) &&
    Number.isFinite(min) &&
    parsedAmount >= min &&
    (!Number.isFinite(max) || parsedAmount <= max);

  const initialAmount = hasValidPrefill ? parsedAmount : min;
  if (!Number.isFinite(initialAmount)) return;

  setRangeSelection((prev) => {
    if (prev?.amount != null) return prev;

    return {
      amount: initialAmount,
      currency: parsedCurrency,
      amountUSD: parsedCurrency === "USD" ? initialAmount : null,
    };
  });
}, [
  formatted?.hasRange,
  formatted?.priceMinValue,
  formatted?.priceMaxValue,
  priceCurrencyStable,
  qsAmount,
  qsCurrency,
]);

  const effectiveAmountUSD = useMemo(() => {
    if (rangeSelection?.amountUSD != null) return Number(rangeSelection.amountUSD);
    if (formatted?.priceUSD != null) return Number(formatted.priceUSD);
    return null;
  }, [rangeSelection?.amountUSD, formatted?.priceUSD]);

  // Pi pricing comes ONLY from the backend quote (face * 1.15 at the live rate).
  // No client-side fee or total: if the quote isn't for this amount yet, it's null
  // and the Pi button stays disabled ("price unavailable").
  const piCtx = useOptionalPiSocketCtx();
  const requestPiQuote = piCtx?.requestPriceUpdate;

  useEffect(() => {
    if (!isPiBrowser || !product?.offerId) return;
    if (!Number.isFinite(effectiveAmountUSD) || effectiveAmountUSD <= 0) return;
    requestPiQuote?.(effectiveAmountUSD, product.offerId);
  }, [isPiBrowser, product?.offerId, effectiveAmountUSD, requestPiQuote]);

  const quoteMatches =
    piCtx?.quotedUSD != null &&
    Number.isFinite(effectiveAmountUSD) &&
    Math.abs(Number(piCtx.quotedUSD) - effectiveAmountUSD) < 0.005;

  const piFeePercent = quoteMatches && Number.isFinite(Number(piCtx?.feePercent)) ? Number(piCtx.feePercent) : null;
  const basePi = quoteMatches && piCtx?.basePI != null ? Number(piCtx.basePI) : null;
  const computedTotalPi = quoteMatches && piCtx?.totalPI != null ? Number(piCtx.totalPI) : null;

  const intentPricing = useMemo(
    () => normalizeCheckoutIntent(checkoutIntent),
    [checkoutIntent]
  );

  // clear stale crypto intent when relevant checkout inputs change
  useEffect(() => {
    setCheckoutIntent(null);
    setActiveRail(null);
  }, [
    product?.offerId,
    rangeSelection?.amount,
    rangeSelection?.currency,
    formData,
    visaEmail,
    visaCountry,
  ]);

  const defaultSender = {
    email: user?.email || "",
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    address: {},
    country: "",
  };

  const cryptoProduct = useMemo(() => {
  if (!product) return null;

  const flat = formData || {};
  const billingAccountId = flat["billing.accountId"]?.trim();
  const recipientFields = Object.entries(flat).map(([field, value]) => ({ field, value }));
  const billing = billingAccountId ? { accountId: billingAccountId } : undefined;
  const referralCode = getReferralCode() ?? undefined; // ← add this

  return {
    ...product,
    offerId: product.offerId || product.sku,
    sku: product.sku,
    productSku: product.sku || product.offerId || "UNKNOWN",
    amountUSD: Number.isFinite(effectiveAmountUSD) ? effectiveAmountUSD : undefined,
    amountMajor: rangeSelection?.amount ?? undefined,
    amountCurrency: rangeSelection?.currency || (formatted.priceCurrency || "USD"),
    recipient: {
      ...(nestedData?.recipient || {}),
      ...(isVisaPrepaidOpen ? { email: visaEmail, country: visaCountry } : {}),
    },
    recipientFields,
    billingAccountId,
    ...(billing ? { billing } : {}),
    country: product?.country || product?.fullDetail?.country,
    ...(referralCode ? { referralCode } : {}), // ← only included when present
  };
}, [
  product,
  formData,
  effectiveAmountUSD,
  rangeSelection?.amount,
  rangeSelection?.currency,
  formatted?.priceCurrency,
  nestedData,
  isVisaPrepaidOpen,
  visaEmail,
  visaCountry,
  // getReferralCode reads localStorage synchronously — no reactive dep needed
]);

  const piProduct = useMemo(() => {
    if (!cryptoProduct) return null;

    return {
      ...cryptoProduct,
      piRate,
      requiredFields: resolvedRequiredFields,
      senderDetails: defaultSender,
    };
  }, [cryptoProduct, piRate, resolvedRequiredFields, defaultSender]);

  if (!product) return null;

  return (
    <div className="max-w-lg mx-auto bg-surface text-textPrimary rounded-xl shadow-md p-6 mt-10 border border-border">
      {isPiBrowser && (
        <div className="mb-5 rounded-xl border border-primary/40 bg-primary/10 px-4 py-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary opacity-80">
              🥧 Pi Network
            </span>
            <span className="text-xs text-textSecondary">Live · Bitget</span>
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-2xl font-bold text-primary">
              1 Pi = ${Number(piRate || 0).toFixed(4)}
            </span>
          </div>
          {computedTotalPi != null && (
            <div className="mt-2 text-sm text-textPrimary">
              This item:{" "}
              <span className="font-semibold text-primary">
                {computedTotalPi.toFixed(4)} Pi
              </span>
              {piFeePercent != null && piFeePercent > 0 && (
                <span className="ml-1 text-xs text-textSecondary">
                  (incl. {piFeePercent}% fee)
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {description && (
        <div className="mb-3 text-sm text-textPrimary leading-relaxed tracking-wide">
          {showFullDescription
            ? description
            : description.slice(0, 100) + ((description?.length ?? 0) > 100 ? "..." : "")}
          {(description?.length ?? 0) > 100 && (
            <button
              onClick={() => setShowFullDescription((prev) => !prev)}
              className="ml-2 text-accent text-xs underline hover:text-primary transition"
            >
              {showFullDescription ? "Show Less" : "Read More"}
            </button>
          )}
        </div>
      )}

      <RedemptionInfo instructions={redemptionInstructions} pdfUrl={brandInfoPdf} />

      <ProductCard
        {...formatted}
        basePI={basePi}
        totalPI={computedTotalPi}
        feePercent={feePercent}
        piRate={piRate}
        subTypes={product.subTypes}
        selectedAmount={rangeSelection?.amount ?? undefined}
        onSelectedAmountChange={handleRangeChange}
      />

      {hardBlocked && (
        <div className="mt-3 text-[12px] bg-warning/10 border border-warning/30 text-warning px-3 py-2 rounded-md">
          This item is temporarily unavailable while our provider finalizes changes.
        </div>
      )}

      {isVisaPrepaidOpen && (
        <div className="mt-3 grid grid-cols-1 gap-3">
          <div>
            <label className="block text-sm mb-1">Recipient Email</label>
            <input
              type="email"
              value={visaEmail}
              onChange={(e) => setVisaEmail(e.target.value)}
              className="w-full min-h-[44px] px-3 py-2 bg-surfaceMuted border border-border rounded-md"
              placeholder="name@example.com"
              required
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Recipient Country (ISO)</label>
            <input
              type="text"
              value={visaCountry}
              onChange={(e) => setVisaCountry(e.target.value.toUpperCase())}
              className="w-full min-h-[44px] px-3 py-2 bg-surfaceMuted border border-border rounded-md"
              placeholder="US"
              required
            />
          </div>
        </div>
      )}

      {effectiveAmountUSD == null && (
        <p className="text-xs text-textSecondary">
          Enter the amount above to calculate the final price in Pi.
        </p>
      )}

      <div className="mt-4 space-y-4">
        <PiRateDisplay rate={piRate} />
        <PiCheckoutInfo amount={computedTotalPi} />

        {!isPiBrowser && intentPricing?.totalUSD != null && (
          <div className="bg-surfaceMuted border border-border rounded-md p-3 text-sm">
            <div className="flex justify-between">
              <span className="text-textSecondary">Provider</span>
              <span className="uppercase">{intentPricing.provider || "crypto"}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-textSecondary">Face Value</span>
              <span>${formatMoney(intentPricing.faceValueUSD) ?? "0.00"}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-textSecondary">
                Fee ({formatMoney(intentPricing.feePercent) ?? "0.00"}%)
              </span>
              <span>${formatMoney(intentPricing.feeUSD) ?? "0.00"}</span>
            </div>

            <div className="flex justify-between font-semibold mt-1">
              <span>Total</span>
              <span>
                ${formatMoney(intentPricing.totalUSD) ?? "0.00"} (
                {formatMoney(intentPricing.totalUSD) ?? "0.00"} {intentPricing.currency})
              </span>
            </div>

            <div className="text-xs text-textSecondary mt-1">
              Amount required to complete checkout.
            </div>
          </div>
        )}

        {showForm && !hardBlocked && (
          <DynamicFieldForm
            requiredFields={reqForForm}
            onSubmit={(values) => {
              if (!values || Object.keys(values).length === 0) return;

              setFormData(values);
              setShowForm(false);

              if (!user) {
                toast.info("Form saved. You can pay with crypto below. Log in to pay with Pi.");
              }
            }}
            countryCode={effectiveCountry}
          />
        )}

        {!showForm && formData && (
          <>
            {hardBlocked ? (
              <button
                type="button"
                disabled
                className="w-full min-h-[44px] flex items-center justify-center cursor-not-allowed opacity-60 bg-surfaceElevated text-textMuted rounded-md"
                title="Temporarily unavailable"
              >
                Unavailable
              </button>
            ) : (
              <>
                {user && piProduct && (
                  <BuyWithPiButton
                    product={piProduct}
                    priceInPi={computedTotalPi}
                    user={user}
                  />
                )}

                {!isPiBrowser && cryptoProduct && (
                  <div className="grid grid-cols-1 gap-2">
                    {user ? (
                      <AppWagmiProvider>
                        {canSeeBase ? (
                          <BasePayButton
                            product={cryptoProduct}
                            user={user}
                            onIntent={onBaseIntent}
                          />
                        ) : (
                          <div className="rounded-lg border border-border bg-surfaceMuted p-3 text-xs text-textSecondary">
                            Base (USDC) checkout is coming soon.
                          </div>
                        )}
                      </AppWagmiProvider>
                    ) : (
                      <div className="rounded-lg border border-warning/30 bg-warning/10 p-3 text-xs text-warning">
                        Log in to pay with USDC on Base (so the purchase appears in your transactions).
                      </div>
                    )}

                    <FogoPayButton
                      product={cryptoProduct}
                      user={user}
                      onIntent={onFogoIntent}
                    />
                         <TronPayButton
                          product={cryptoProduct}
                          user={user}
                          onIntent={onTronIntent}
                        />
                    <NowPaymentsButton product={cryptoProduct} user={user} />
                  </div>
                )}
              </>
            )}

       {!user && !hardBlocked && (
          <div className="bg-surface p-4 rounded-md border border-border mt-2 space-y-3">
            <p className="text-textSecondary">
              Use Pi Browser for Pi Coin payments. You can still try paying with crypto,
              or log in for the standard checkout flow.
            </p>

            {!isPiBrowser && (
              <div className="rounded-lg border border-border bg-surface/60 p-3">
                <p className="text-sm text-center mb-3">
                  Paying with Pi? Open this exact product in Pi Browser.
                </p>

                <PiBrowserCta
                  variant="compact"
                  path={piBrowserPath}
                  query={piBrowserQuery}
                  label="🚀 Continue in Pi Browser"
                />
              </div>
            )}

            <LoginButton />
          </div>
        )}
          </>
        )}

        {onCancel && (
          <button
            className="mt-2 inline-flex min-h-[44px] items-center text-sm text-accent hover:underline"
            onClick={onCancel}
          >
            Cancel Selection
          </button>
        )}
      </div>
    </div>
  );
}